import { SalesContract } from "src/api/generated/erp/db/types/tables/salesContract";
import {
	CustomerSalesContractEditApi,
	CustomerSalesContractEditApi_FormInitData,
} from "src/api/generated/erp/sales/pricing/api/customerSalesContractEditApi";
import { FormCommonProps } from "src/components/common/forms/types.ts";
import { FormTextField } from "src/components/common/forms/fields/FormTextField.tsx";
import { FormDateField } from "src/components/common/forms/fields/FormDateField.tsx";
import i18n from "i18next";
import { requireRule } from "src/components/common/forms/validation.ts";
import { AsyncForm } from "src/components/common/forms/AsyncForm.tsx";
import { DeepPartial } from "react-hook-form";
import { FormSelectField } from "src/components/common/forms/fields/FormSelectField.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface CustomerSalesContractFormProps extends FormCommonProps<number> {
	customerId: number;
	contract?: SalesContract;
}

export const CustomerSalesContractForm = ({
	customerId,
	contract,
	onCompleted,
	onFormEdited,
}: CustomerSalesContractFormProps) => {
	return (
		<AsyncForm<CustomerSalesContractEditApi_FormInitData, SalesContract, number>
			fetch={() =>
				CustomerSalesContractEditApi.getFormInitData({
					customerId,
					salesContractId: contract?.salesContractId,
				})
			}
			getDefaultValues={getDefaultValues}
			submit={submit}
			onCompleted={onCompleted}
			onFormEdited={onFormEdited}
			agentContext={{
				key: defineAgentUiContextKey("erp.sales.pricing.customerSalesContract.form"),
				title: i18n.t("sales_contract"),
				data: {
					customerId,
					salesContractId: contract?.salesContractId ?? null,
				},
			}}
			render={({ control, data: { currencyOptions } }) => (
				<>
					<FormSelectField
						control={control}
						name={"currencyId"}
						label={i18n.t("currency")}
						options={currencyOptions}
						getOptionKey={(currency) => currency.currencyId}
						getOptionLabel={(currency) => currency.currencyCode}
						disableClearable
						disabled={contract != null}
						rules={requireRule()}
					/>
					<FormTextField control={control} name="name" label={i18n.t("name")} rules={requireRule()} />
					<FormDateField
						control={control}
						name={"activeFrom"}
						label={i18n.t("active_from")}
						rules={requireRule()}
					/>
					<FormDateField
						control={control}
						name={"activeTo"}
						label={i18n.t("active_to")}
						rules={requireRule()}
					/>
				</>
			)}
		/>
	);

	function getDefaultValues(data: CustomerSalesContractEditApi_FormInitData): DeepPartial<SalesContract> {
		return (
			data.contract ?? {
				customerId,
				currencyId: data.defaultCurrencyId,
				description: "",
			}
		);
	}

	async function submit(values: SalesContract) {
		if (contract == null) {
			return CustomerSalesContractEditApi.insert({
				contract: values,
			});
		}

		await CustomerSalesContractEditApi.update({
			contract: values,
		});
		return contract.salesContractId;
	}
};
